import React from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';

import { config } from '@config/pendo.config.json';
import { black, feedbackGreenLight, feedbackOrangeLight, gray5 } from 'styles/global_defaults/colors';
import { normalFontWeight, openSans, semiBoldFontWeight, textMediumFontSize, textMediumLineHeight, textSmallLineHeight } from 'styles/global_defaults/fonts';
import { halfSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { moss, sandstone } from 'athena/styles/colors';

import NvIcon from 'shared/components/nv-icon';
import type { SectionContent } from 'redux/schemas/models/submissions';

export type SectionKind = 'did-well' | 'can-grow' | 'worked' | 'improved';

type Props = {
  kind: SectionKind;
  content?: SectionContent;
};

type SectionConfig = {
  iconName: string;
  iconColor: string;
  iconBgColor: string;
  labelKey: () => string;
  dataQa: string;
};

const parseBullets = (content: string | string[]): string[] | null => {
  if (Array.isArray(content)) {
    return content;
  }
  if (!content.trim().startsWith('[')) {
    return null;
  }
  try {
    const parsed = JSON.parse(content);
    if (Array.isArray(parsed) && parsed.every(item => typeof item === 'string')) {
      return parsed;
    }
  } catch {
    // Not a JSON array — fall back to rendering the raw string.
  }
  return null;
};

const getConfig = (kind: SectionKind): SectionConfig => {
  switch (kind) {
    case 'did-well':
      return {
        iconName: 'like',
        iconColor: moss,
        iconBgColor: feedbackGreenLight,
        labelKey: t.SUBMISSIONS.AI_ASSIGNMENT.DID_WELL,
        dataQa: config.pendo.submissionView.aiInsights.sectionDidWell,
      };
    case 'can-grow':
      return {
        iconName: 'ideas',
        iconColor: sandstone,
        iconBgColor: feedbackOrangeLight,
        labelKey: t.SUBMISSIONS.AI_ASSIGNMENT.CAN_GROW,
        dataQa: config.pendo.submissionView.aiInsights.sectionCanGrow,
      };
    case 'worked':
      return {
        iconName: 'like',
        iconColor: moss,
        iconBgColor: feedbackGreenLight,
        labelKey: t.SUBMISSIONS.AI_ASSIGNMENT.WORKED,
        dataQa: config.pendo.submissionView.aiInsights.sectionWorked,
      };
    case 'improved':
    default:
      return {
        iconName: 'ideas',
        iconColor: sandstone,
        iconBgColor: feedbackOrangeLight,
        labelKey: t.SUBMISSIONS.AI_ASSIGNMENT.IMPROVED,
        dataQa: config.pendo.submissionView.aiInsights.sectionImproved,
      };
  }
};

const styles = css`
  display: flex;
  flex-direction: column;

  .section-header {
    display: flex;
    align-items: center;
    gap: ${halfSpacing}px;
    padding: ${halfSpacing}px 0;
    border-bottom: 1px solid ${gray5};
  }

  .section-icon {
    width: 36px;
    height: 36px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .section-label {
    margin: 0;
    font-family: ${openSans};
    font-weight: ${semiBoldFontWeight};
    font-size: ${textMediumFontSize}px;
    line-height: ${textSmallLineHeight}px;
    color: ${black};
  }

  .section-body {
    padding: ${halfSpacing}px 0 0 ${standardSpacing + 4}px;
    font-family: ${openSans};
    font-weight: ${normalFontWeight};
    font-size: ${textMediumFontSize}px;
    line-height: ${textMediumLineHeight}px;
    color: ${black};
  }

  .section-bullets {
    margin: 0;
    padding-left: ${standardSpacing}px;

    li:not(:last-child) {
      margin-bottom: ${halfSpacing}px;
    }
  }
`;

const Section: React.FC<Props> = ({ kind, content }) => {
  if (!content) {
    return null;
  }

  const bullets = parseBullets(content);
  if (bullets && bullets.length === 0) {
    return null;
  }

  const { iconName, iconColor, iconBgColor, labelKey, dataQa } = getConfig(kind);

  const iconWrapperCss = css`
    background-color: ${iconBgColor};
    color: ${iconColor};
  `;

  return (
    <div css={styles} data-qa={dataQa}>
      <div className='section-header'>
        <div className='section-icon' css={iconWrapperCss}>
          <NvIcon icon={iconName} size='smallest' aria-hidden />
        </div>
        <h3 className='section-label'>{labelKey()}</h3>
      </div>
      <div className='section-body'>
        {bullets ? (
          <ul className='section-bullets'>
            {bullets.map((bullet, index) => (
              // eslint-disable-next-line react/no-array-index-key, react/no-danger
              <li key={index} dangerouslySetInnerHTML={{ __html: bullet }} />
            ))}
          </ul>
        ) : (
          // eslint-disable-next-line react/no-danger
          <span dangerouslySetInnerHTML={{ __html: Array.isArray(content) ? content.join('') : content }} />
        )}
      </div>
    </div>
  );
};

export default Section;
